import { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface FeatureTooltipProps {
  children: ReactNode;
  content: string;
  side?: "top" | "right" | "bottom" | "left";
}

export function FeatureTooltip({ children, content, side = "top" }: FeatureTooltipProps) {
  return (
    <TooltipProvider delayDuration={500}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Pre-defined tooltip contents for common features
export const TOOLTIP_CONTENT = {
  SEARCH: "Search through both source text and translations. Use keywords, phrases, or language codes to find specific entries.",
  
  ADVANCED_FILTERS: "Filter by languages, date ranges, categories, and tags. Combine multiple filters for precise results.",
  
  BULK_SELECT: "Enable bulk selection to manage multiple translations at once. Select entries with checkboxes and perform batch operations.",
  
  FAVORITES: "Star important translations to save them for quick access. Use the Saved tab to view all favorites.",
  
  CATEGORIES: "Organize translations into categories like 'Work', 'Travel', or 'Learning'. Click to set or change the category.",
  
  TAGS: "Add multiple tags to translations for detailed organization. Use tags like #urgent, #formal, or #business.",
  
  EXPORT: "Download your translation history in JSON (best for re-importing) or CSV (for spreadsheets) format.",
  
  IMPORT: "Upload translation data from JSON or CSV files. Supports drag-and-drop and includes preview before importing.",
  
  COPY: "Copy text to your clipboard. Click to copy the translation, or use the source text copy button.",
  
  DELETE: "Remove this translation from your history. This action cannot be undone.",
  
  DATE_FILTER: "Select a date range to filter translations. Find translations from specific days, weeks, or months.",
  
  LANGUAGE_FILTER: "Filter by source or target language. Choose 'Any language' to see all translations.",
  
  CLEAR_FILTERS: "Remove all active filters and search terms to show all translations.",
};